import { z } from 'zod';
import type { CounterpartyType } from './counterparties';
import { isInternalEmail } from './email';
import type { Role } from './enums';
import type { ALL_SYSTEM_GRANT_CODES } from './grants';

// ── Пожелание по роли в заявке на регистрацию ──
// Человек выбирает, кем работает; права из этого не следуют (ADR 0034). Роль назначает
// администратор при активации — иначе саморегистрация выдавала бы доступ, а признак «заявка =
// не активна и без роли» перестал бы работать. Поэтому это отдельный перечень, а не `Role`:
// у части вариантов роли в портале нет вовсе, а называются они на языке заявителя.
//
// Перечень называет **должности, а не роли портала**, и только так он переживает реформу доступа:
// после её шага cleanup имён `rukstroy` и `commandant` в `ROLES` не останется, а руководитель
// строительства и комендант никуда не денутся — так они себя называют и так же назовут при
// регистрации.
//
// Порядок массива — порядок списка на экране регистрации: свои по месту работы (площадка, отдел),
// диспетчер, внешние исполнители, водитель и «другое». С порядком значений enum'а в базе он не
// совпадает и совпадать не обязан: `ALTER TYPE ... ADD VALUE` дописывает в конец, сортировки по
// этому типу в портале нет ни одной, а сверка кода с базой сравнивает множества.

export const REGISTRATION_ROLE_REQUESTS = [
  // Площадка: три должности, ведущие в одну роль. Врозь они держатся потому, что различает их не
  // роль, а полномочия (см. таблицу активации ниже): слей мы их в одного «сотрудника объекта» —
  // и по заявке было бы не понять, кому давать визу, а кому и заказ техники закрыт.
  'site_staff',
  'rukstroy',
  'commandant',
  // Отдел (ADR 0040, 0062, 0123) — офисный заказчик техники и оргтехники, у которого пожелания не
  // было вовсе: он выбирал «Другое», то есть ровно тот звонок, ради отмены которого пожелания и
  // заводили.
  'department_staff',
  'department_head',
  'dispatcher',
  // Внешние исполнители работают от лица контрагента (ADR 0010) и называют себя организацией, от
  // которой пришли. Роль у всех трёх одна, а что именно учётка исполняет, решает тип контрагента —
  // `expectedCounterpartyType` ниже.
  'waste_operator',
  'vehicle_lessor',
  'service_company',
  // Водитель (ADR 0102): единственное пожелание, чья роль совпадает с ним буквально. Отдельной
  // кнопки «Я водитель» на экране входа нет — человек выбирает себя в общем списке.
  'driver',
  'other',
] as const;
export const registrationRoleRequestSchema = z.enum(REGISTRATION_ROLE_REQUESTS);
export type RegistrationRoleRequest = (typeof REGISTRATION_ROLE_REQUESTS)[number];

export const registrationRoleRequestLabels: Record<RegistrationRoleRequest, string> = {
  // «Штаб» — название из таблицы прав; заявителю оно ничего не говорит.
  site_staff: 'Сотрудник объекта',
  rukstroy: 'Руководитель строительства',
  // Комендант, наоборот, назван своей должностью: так он себя и называет. Отдельным вариантом, а
  // не внутри «сотрудника объекта», — заказ техники ему закрыт, и по пожеланию администратор
  // видит, какие полномочия к роли площадки выдавать, а какие нет.
  commandant: 'Комендант',
  department_staff: 'Сотрудник отдела',
  department_head: 'Руководитель отдела',
  dispatcher: 'Диспетчер',
  waste_operator: 'Оператор по вывозу мусора',
  vehicle_lessor: 'Оператор по аренде техники',
  // Подпись называет человека, а код — организацию, от лица которой он работает, и это не
  // рассогласование: сама сервисная компания себя не регистрирует, её заводит администрация в
  // справочнике контрагентов (ответ заказчика 28.08.2026), а регистрируется её сотрудник обычным
  // порядком. Код назван по правилу двух соседей выше — третий, названный иначе, читался бы как
  // исключение там, где его нет. Модуль в скобках потому, что «сервисная компания» сама по себе не
  // говорит ничего: обслуживание оргтехники (ADR 0085) — единственное, что она в портале делает.
  service_company: 'Представитель сервисной компании (обслуживание оргтехники)',
  // Как человек себя называет; машинист и тракторист выберут её же — кабинет открыт любой
  // должности справочника (ADR 0102).
  driver: 'Водитель',
  other: 'Другое',
};

/**
 * Подписи упразднённых пожеланий — тех, что с экрана регистрации сняты, но стоят в живых строках
 * `users.requested_role`.
 *
 * **Зачем отдельный словарь, а не строка в `registrationRoleRequestLabels`.** Упразднение — это
 * снятие значения из `REGISTRATION_ROLE_REQUESTS`, после которого
 * `Record<RegistrationRoleRequest, string>` его не принимает: тип обязан отвечать за действующий
 * перечень и только за него, иначе экран регистрации предложит выбрать то, чего в портале уже нет.
 * А подпись всё равно нужна — значение никуда не делось.
 *
 * **Где оно остаётся.** Значение enum'а в Postgres не удаляется вовсе (`ALTER TYPE ... DROP VALUE`
 * не существует), и на нём стоят заявки и учётки: пожелание — это то, что заявитель о себе сказал,
 * и переписать его задним числом нечем.
 *
 * **Сегодня словарь пуст, и это правильное состояние, а не заготовка**: ни одно пожелание не
 * упраздняется, перечень только пополняется. Заведён заранее затем, что вместе с ним заводится
 * страж, требующий подпись **на каждое значение enum'а в базе**, — покраснеет он на том релизе,
 * который значение снимет, а подпись перенести забудет. Появись словарь тем же релизом, что и
 * перенос, страж проверял бы работу, сделанную минуту назад.
 *
 * Тип ключа — `string`, а не `RegistrationRoleRequest`: в том и смысл, что ключи здесь — значения,
 * которых в перечне уже нет. Это тот же приём, которым живут роли (`ARCHIVED_ROLE_LABELS`), и
 * второго его устройства здесь не заводится.
 */
export const ARCHIVED_REQUEST_ROLE_LABELS: Readonly<Record<string, string>> = {};

/**
 * Подпись пожелания по значению из базы — действующего или упразднённого.
 *
 * Спрашивают этой функцией там, где пожелание приходит **строкой учётки**: список заявок, карточка,
 * письма, журнал изменений. Прямой `registrationRoleRequestLabels[value]` ответил бы там
 * `undefined` в день упразднения, и вместо пожелания появилась бы пустота — в заявке, которую уже
 * не переписать. Где пожелание пришло выбором в форме и типизировано `RegistrationRoleRequest`,
 * обращение остаётся прямым: там подпись обязана существовать, и `undefined` означал бы забытую
 * строку словаря, а не историю.
 *
 * Неизвестное значение возвращается как есть, а не «—»: сырой код читается хуже подписи, но лучше
 * прочерка — по нему хотя бы видно, о чём речь. Тем же ответом отрисуется заявка с новым
 * пожеланием в откаченном портале: сборка постарше подписи для него ещё не знает.
 */
export function requestRoleTitle(value: string): string {
  return (
    registrationRoleRequestLabels[value as RegistrationRoleRequest] ??
    ARCHIVED_REQUEST_ROLE_LABELS[value] ??
    value
  );
}

/**
 * Код набора системного каталога.
 *
 * Выведен из самого списка кодов, а не собран объединением четырёх типов каталога: состав каталога
 * объявлен в `grants.ts` один раз, и повторять его здесь значило бы завести пятый перечень, тихо
 * расходящийся с четырьмя. Импорт типовой — `import type` стирается компилятором и ребра в графе
 * модулей не создаёт: `grants.ts` тянет за собой словарь прав, каталог и надстройки, а здесь нужно
 * одно только имя типа.
 *
 * Тип не сужен до `RoleGrantCode` намеренно, хотя сегодня в таблице умолчаний стоят только наборы,
 * замещающие роли: подставить пожеланию модульный набор (расходники, аудит) можно и без всякой
 * реформы ролей, а сужение объявило бы таблицу её частью — чем она не является.
 */
type GrantCode = (typeof ALL_SYSTEM_GRANT_CODES)[number];

/** Чем форма рассмотрения заполняется по пожеланию. */
export interface ActivationDefaults {
  /** `null` — умолчания нет, роль выбирает администратор. */
  readonly role: Role | null;
  /** Пустой список — «роль без полномочий», а не «полномочия неизвестны». */
  readonly grants: readonly GrantCode[];
}

/**
 * Умолчания формы рассмотрения заявки: что подставить, увидев пожелание, — роль и коды наборов
 * к ней.
 *
 * **Это таблица подстановки, а не отображение «пожелание = роль»** (§11.1 плана реструктуризации
 * прав). Разница не словесная: решает администратор, а таблица лишь заполняет поля формы до того,
 * как он их тронул. Отсюда и то, что `null` — рабочее значение, а не дыра: «другое» роли не
 * подразумевает вовсе, и форма просто открывается с пустым выбором. Никакой код по этой таблице
 * прав не выдаёт и учёток не активирует — активацией остаётся `PATCH` карточки с явно выбранными
 * ролью и наборами, а «Активен» подстановка не заполняет никогда.
 *
 * **Состав наборов взят не из головы: это строки `ROLE_MIGRATIONS`** (`role-migration.ts`),
 * которыми реформа переводит упраздняемые роли. Иначе новая учётка «сотрудника объекта» получала
 * бы не то, что действующий штаб после перевода, и разница объяснялась бы одним лишь тем, кто
 * когда зарегистрировался. Совпадение держит тест, а не памятка: таблицы две, и разъехаться они
 * могут молча.
 *
 * **Что будет, когда роль из таблицы упразднят.** Роли реформы сливаются (§9, §15 плана), и
 * значение вроде `shtab` однажды исчезнет из `ROLES`. Тогда эта таблица перестанет собираться
 * компилятором — литерал не подойдёт под тип `Role`, — и это её главное свойство, а не помеха:
 * решение принимает человек, и принять его нужно ровно один раз, в момент слияния. Вариантов два и
 * оба законны: перенацелить умолчание на роль, в которую слили прежнюю, добавив к ней наборы, в
 * которые переехало различие, либо поставить `null` — «умолчания нет, администратор выбирает сам».
 * Чего делать нельзя — оставить имя упразднённой роли: форма подставила бы значение, которого в
 * перечне нет, портал показал бы пустой выбор заполненным, а сервер отверг бы сохранение схемой.
 * Проверку «ни одно умолчание не указывает на несуществующую и на упраздняемую роль» держит
 * `registration-request-contracts.test.ts` — на случай, если тип когда-нибудь ослабят.
 *
 * Таблица закрытая, наружу смотрит `activationDefaultsFor` — тем же приёмом, что
 * `requestExpectsCorporateEmail` ниже: читателю нужен ответ на своё пожелание, а не сам словарь, и
 * заявка без пожелания вовсе отвечается функцией, а не отдельным случаем у каждого вызывающего.
 *
 * **Почему имя сменилось.** Прежний `defaultRoleForRequest` отвечал одной ролью. Функция,
 * называющаяся «роль по умолчанию» и возвращающая полномочия, врёт в имени — а полномочия здесь
 * главное: после реформы штаб от коменданта и руководитель отдела от сотрудника отличаются только
 * ими.
 */
const ACTIVATION_DEFAULTS: Record<RegistrationRoleRequest, ActivationDefaults> = {
  /*
   * Три площадочных пожелания ведут в одну роль `site`: прежние `shtab`, `rukstroy` и `commandant`
   * упраздняются реформой, и умолчание перенацелено на роль, в которую их сливают (шаг prepare
   * этапа 8, ADR 0113). Оставь мы прежние имена — форма открывалась бы с ролью, которую сервер
   * отвергает, то есть заявку нельзя было бы принять, не разобравшись сперва в реформе.
   *
   * Различие трёх должностей при этом не пропало, а переехало в наборы — ровно те, которыми
   * перевод ролей отличает штаб от коменданта. ADR 0112 §8 держал умолчания без наборов по
   * причине «подставлять их форме пока нечем»; эта причина исчерпана здесь.
   */
  /*
   * Механизация приезжает набором всем трём площадочным пожеланиям (план
   * `docs/mechanization-approval-and-grants-plan.md`, Р8): модуль ушёл из состава роли `site`, и
   * без подстановки форма открывалась бы состоянием, которого администратор не заказывал, — новый
   * штаб без аренды, о которой его никто не спросил, а у руководителя строительства ещё и виза без
   * чтения, то есть набор, который сервер откажется сохранить.
   *
   * Подстановка — не выдача: администратор видит отмеченные наборы и снимает лишнее до сохранения.
   * Ровно этим назначаемость и отличается от права в составе роли.
   */
  site_staff: { role: 'site', grants: ['vehicle_ordering', 'mech_ordering'] },
  // Виза отдельным набором, а не внутри «Заказа техники»: подписывает не всякий, кто заказывает.
  rukstroy: { role: 'site', grants: ['vehicle_ordering', 'mech_ordering', 'site_approval'] },
  // Комендант — `site` плюс одна аренда: заказа техники у него нет, а компрессор и тепловая пушка
  // на площадке нужны тому же, кто отвечает за её быт (ADR 0152, Р9).
  commandant: { role: 'site', grants: ['mech_ordering'] },
  department_staff: { role: 'department', grants: [] },
  /*
   * Роль `department_head` в умолчании не названа, хотя пожелание называется так же: она
   * упраздняется этапом 9, и подставлять её значило бы открывать форму значением, которое сервер
   * отклоняет (`retiringRoleIssue`). Виза отдела приезжает набором — тем самым, которым её выдаёт
   * перевод.
   *
   * Признак «руководит отделом» подстановка не ставит вовсе: это свойство связи учётки с отделом
   * (`user_departments.is_head`), и ставится оно в карточке отдела. Виза — право, а не назначение.
   */
  department_head: { role: 'department', grants: ['department_approval'] },
  dispatcher: { role: 'dispatcher', grants: [] },
  /*
   * Три внешних пожелания ведут к одной роли `operator` и с пустыми наборами: что именно исполняет
   * учётка — вывоз мусора, аренду техники или обслуживание оргтехники, — задаёт не роль и не
   * набор, а тип контрагента (ADR 0038). Различить их подстановке всё же нужно, и делает это
   * `expectedCounterpartyType` ниже: он правит подбор организации, а не роль.
   */
  waste_operator: { role: 'operator', grants: [] },
  vehicle_lessor: { role: 'operator', grants: [] },
  service_company: { role: 'operator', grants: [] },
  driver: { role: 'driver', grants: [] },
  other: { role: null, grants: [] },
};

/**
 * Умолчание для заявки без пожелания — учётку завёл администратор сам, и подставлять не с чего.
 *
 * Своя константа, а не строка `other`: сегодня ответы совпадают, но вопросы разные — «человек
 * назвался, и мы не поняли» против «человек ничего не говорил», — и первое же умолчание, выданное
 * «другому», досталось бы заодно всем учёткам, заведённым руками.
 */
const NO_ACTIVATION_DEFAULTS: ActivationDefaults = { role: null, grants: [] };

/**
 * Чем форма рассмотрения заполняется по пожеланию: роль (или `null` — выбор за администратором) и
 * коды наборов к ней. Пожелания у заявки может не быть вовсе — это тот же полный ответ, а не
 * отдельный случай для вызывающего.
 */
export function activationDefaultsFor(
  request: RegistrationRoleRequest | null | undefined,
): ActivationDefaults {
  return request ? ACTIVATION_DEFAULTS[request] : NO_ACTIVATION_DEFAULTS;
}

/**
 * Тип контрагента, которого ждёт пожелание; `null` — контрагента у него не спрашивают вовсе.
 *
 * Роль у трёх внешних пожеланий одна (`operator`), а что учётка исполняет, решает тип контрагента
 * (ADR 0038). Отсюда и таблица: подбор организации по свободному тексту заявки обязан считаться
 * **только внутри ожидаемого типа**. «Ромашка» среди арендодателей и «Ромашка» среди сервисных
 * компаний — две разные организации; подбор без типа выбрал бы не ту и выдал учётке не тот модуль,
 * и сделал бы это молча — администратор увидел бы заполненное поле с правдоподобным названием.
 *
 * Сам список контрагентов в форме этим не сужается: администратор вправе решить, что заявитель
 * ошибся должностью, и выбрать организацию другого типа руками. Тип правит подстановку, а не выбор.
 *
 * Таблица отдельная, а не вывод из `registrationRequestDetail`: совпадение с «спрашиваем компанию»
 * сегодня полное, но вопросы разные — «что уточнить у заявителя» против «где искать организацию», —
 * и связывать их значило бы обещать тип всякому будущему пожеланию про компанию.
 */
const REQUEST_COUNTERPARTY_TYPE: Record<RegistrationRoleRequest, CounterpartyType | null> = {
  site_staff: null,
  rukstroy: null,
  commandant: null,
  department_staff: null,
  department_head: null,
  dispatcher: null,
  waste_operator: 'operator',
  vehicle_lessor: 'vehicle_lessor',
  service_company: 'service',
  // Контрагент есть и у водителя стороннего парка, но пожелание о нём не спрашивает: привязку
  // делает администратор при активации (ADR 0102), а свободного поля «Компания» у водителя нет.
  driver: null,
  other: null,
};

export function expectedCounterpartyType(
  request: RegistrationRoleRequest | null | undefined,
): CounterpartyType | null {
  return request ? REQUEST_COUNTERPARTY_TYPE[request] : null;
}

/**
 * Что спросить дополнительно, чтобы пожелание было осмысленным.
 *
 * `object` и `department` — два вопроса к **одному** полю (`requestedObject`): колонка отвечает на
 * «где вы работаете», а объект это или отдел, различается подписью на экране и текстом справки в
 * карточке. Четвёртого поля ради синонима не заводится: смысл колонки не меняется, а миграция
 * данных стоила бы дороже подписи.
 */
export type RegistrationRequestDetail = 'none' | 'object' | 'department' | 'company' | 'comment';

export const registrationRequestDetail: Record<RegistrationRoleRequest, RegistrationRequestDetail> =
  {
    // Объектные роли работают в пределах объекта (ADR 0025) — без него заявку не рассмотреть.
    site_staff: 'object',
    rukstroy: 'object',
    commandant: 'object',
    // Отдел ограничивает офисного заказчика тем же способом, что объект — площадку (ADR 0062).
    department_staff: 'department',
    department_head: 'department',
    dispatcher: 'none',
    // Оператор работает от лица контрагента (ADR 0010).
    waste_operator: 'company',
    vehicle_lessor: 'company',
    service_company: 'company',
    // Ни объекта, ни компании у водителя не спрашивают: он работает от парка, а привязку к
    // карточке справочника делает администратор при активации (ADR 0102).
    driver: 'none',
    // «Другое» — единственное пожелание, у которого нет и роли по умолчанию
    // (`activationDefaultsFor('other').role === null`). Без объяснения своими словами такая заявка
    // не содержит ничего, кроме ФИО и адреса: администратору не из чего выбирать роль, и решается
    // она звонком — ровно тем, ради отмены которого пожелание и заводили (ADR 0034).
    other: 'comment',
  };

/**
 * Ждут ли от заявителя рабочий адрес в домене компании (ADR 0090).
 *
 * У внешних исполнителей — нет: они работают от лица сторонней организации (ADR 0010), рабочая
 * почта у них по определению не наша, и предупреждение о «внешнем» адресе требовало бы от них
 * невозможного. У «другого» — да: так себя называет чаще свой сотрудник, которому роль подберут
 * при рассмотрении.
 *
 * Таблица отдельная, а не вывод из `registrationRequestDetail`: совпадение с «спрашиваем компанию»
 * случайное — и уже разошлось на отделе, — а следующее пожелание сломало бы вывод молча.
 */
const requestExpectsCorporateEmail: Record<RegistrationRoleRequest, boolean> = {
  site_staff: true,
  rukstroy: true,
  commandant: true,
  /*
   * Отдел рабочей почты не ждёт — решение опроса 28.08.2026 о новых пожеланиях. Асимметрия со
   * площадкой при этом настоящая: свои сотрудники есть и там и там, а пометку «Внешняя почта»
   * получит только объектный. Она объявлена риском плана, а не недосмотром, и разворачивается
   * этими двумя строками — признак ничего не запрещает, он лишь просит приглядеться.
   */
  department_staff: false,
  department_head: false,
  dispatcher: true,
  waste_operator: false,
  vehicle_lessor: false,
  service_company: false,
  // Почта у водителя личная по определению: предупреждение о «внешнем» адресе требовало бы от
  // него невозможного.
  driver: false,
  other: true,
};

export function expectsCorporateEmail(request: RegistrationRoleRequest): boolean {
  return requestExpectsCorporateEmail[request];
}

/**
 * Заявка подана с чужого адреса там, где ждали рабочий. Отказом это не является — признак нужен
 * администратору, чтобы приглядеться к заявке, и заявителю, чтобы одуматься до отправки.
 */
export function isExternalRegistrationEmail(value: {
  email: string;
  requestedRole: RegistrationRoleRequest | null;
}): boolean {
  if (!value.requestedRole || !expectsCorporateEmail(value.requestedRole)) return false;
  return !isInternalEmail(value.email);
}

/** Уточнение — свободный текст: справочники неаутентифицированному не отдаются (ADR 0034). */
const detailField = z.string().trim().max(200);

export const registrationRequestFields = {
  requestedRole: registrationRoleRequestSchema,
  // Подразделение, где человек работает: объект или отдел — колонка одна на оба вопроса.
  requestedObject: detailField.default(''),
  requestedCompany: detailField.default(''),
  requestedComment: detailField.default(''),
};

export interface RegistrationRequestInput {
  requestedRole: RegistrationRoleRequest;
  requestedObject: string;
  requestedCompany: string;
  requestedComment: string;
}

/** Пропущенное обязательное уточнение; `null` — заявка полна. */
export function registrationRequestIssue(
  value: RegistrationRequestInput,
): { field: 'requestedObject' | 'requestedCompany' | 'requestedComment'; message: string } | null {
  const detail = registrationRequestDetail[value.requestedRole];
  if (detail === 'object' && !value.requestedObject.trim()) {
    return { field: 'requestedObject', message: 'Укажите объект' };
  }
  // Ветка своя, хотя поле то же: заявителю показывается ровно тот вопрос, который ему задали, и
  // «Укажите объект» под полем «Отдел» читалось бы как ошибка формы.
  if (detail === 'department' && !value.requestedObject.trim()) {
    return { field: 'requestedObject', message: 'Укажите отдел' };
  }
  if (detail === 'company' && !value.requestedCompany.trim()) {
    return { field: 'requestedCompany', message: 'Укажите название компании' };
  }
  if (detail === 'comment' && !value.requestedComment.trim()) {
    return { field: 'requestedComment', message: 'Напишите, кем вы работаете' };
  }
  return null;
}

/**
 * Стирает уточнение, которого выбранное пожелание не требует: «компания» у диспетчера — мусор.
 *
 * Подразделение переживает оба вопроса — `object` и `department` пишут в одну колонку, и разбирать
 * их здесь было бы всё равно что стирать поле, которое сам же и заполнил.
 *
 * **Комментарий не стирается ни у кого.** Он открыт всем пожеланиям как способ объясниться: узкие
 * должности (ИТ-служба, ведение оргтехники, механик ОГМ) своего пожелания не получают и приходят
 * «сотрудником отдела», дописывая словами, кем работают. Таблица «кому показывать комментарий»
 * была бы правилом, которое придётся вспоминать при каждом новом пожелании, а стёртое объяснение
 * восстановить нечем. Обязательным комментарий остаётся ровно у `other` — это и есть
 * `detail === 'comment'`.
 */
export function normalizeRegistrationRequest<T extends RegistrationRequestInput>(value: T): T {
  const detail = registrationRequestDetail[value.requestedRole];
  return {
    ...value,
    requestedObject: detail === 'object' || detail === 'department' ? value.requestedObject : '',
    requestedCompany: detail === 'company' ? value.requestedCompany : '',
  };
}
